import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  Target, 
  TrendingUp, 
  AlertTriangle, 
  Lightbulb,
  CheckCircle2,
  XCircle,
  ArrowRight,
  BookOpen,
  Sparkles,
  Brain
} from 'lucide-react';

const AssessmentFeedback = ({ resultId, onClose }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/rpat/assessments/results/${resultId}/feedback`);
      setFeedback(res.data);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [api, resultId]);

  useEffect(() => {
    if (resultId) {
      fetchFeedback();
    }
  }, [fetchFeedback, resultId]);

  if (loading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Brain className="h-6 w-6 animate-pulse" />
            <div>
              <CardTitle>Generating Your Feedback...</CardTitle>
              <p className="text-sm text-white/80 mt-1">Our AI is analyzing your performance</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    );
  }

  if (error || !feedback) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <p className="text-muted-foreground">{error || 'Feedback not available'}</p>
          {onClose && (
            <Button variant="outline" onClick={onClose} className="mt-4">
              Close
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const { ai_feedback } = feedback;
  const readinessColors = {
    ready: 'from-emerald-500 to-green-500',
    almost_ready: 'from-amber-500 to-orange-500',
    needs_practice: 'from-red-500 to-rose-500'
  };
  const readinessLabels = {
    ready: 'Ready to Progress!',
    almost_ready: 'Almost There!',
    needs_practice: 'Keep Practicing'
  };

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <CardHeader className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6" />
            <div>
              <CardTitle>360° AI Feedback Report</CardTitle>
              <p className="text-sm text-white/80 mt-1">
                {ai_feedback.generated ? 'AI-Powered Analysis' : 'Basic Analysis'}
              </p>
            </div>
          </div>
          <Badge className={`bg-gradient-to-r ${readinessColors[ai_feedback.estimated_readiness] || readinessColors.needs_practice} text-white border-0`}>
            {readinessLabels[ai_feedback.estimated_readiness] || 'Keep Learning'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-6">
        {/* Score Breakdown */}
        {ai_feedback.score_breakdown && (
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">
                {ai_feedback.score_breakdown.correct}
              </div>
              <div className="text-xs text-muted-foreground">Correct</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-500">
                {ai_feedback.score_breakdown.incorrect}
              </div>
              <div className="text-xs text-muted-foreground">Incorrect</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-violet-600">
                {ai_feedback.score_breakdown.percentage?.toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">Score</div>
            </div>
          </div>
        )}

        {/* Overall Assessment */}
        {ai_feedback.overall_assessment && (
          <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-cyan-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-cyan-900 mb-1">Overall Assessment</h4>
                <p className="text-sm text-cyan-800">{ai_feedback.overall_assessment}</p>
              </div>
            </div>
          </div>
        )}

        {/* Strengths */}
        {ai_feedback.strengths?.length > 0 && (
          <div>
            <h4 className="flex items-center gap-2 font-semibold text-emerald-700 mb-3">
              <TrendingUp className="h-5 w-5" />
              Your Strengths
            </h4>
            <div className="space-y-2">
              {ai_feedback.strengths.map((strength, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-emerald-900">{strength.area}</div>
                    <div className="text-sm text-emerald-700">{strength.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Weaknesses */}
        {ai_feedback.weaknesses?.length > 0 && (
          <div>
            <h4 className="flex items-center gap-2 font-semibold text-amber-700 mb-3">
              <AlertTriangle className="h-5 w-5" />
              Areas for Improvement
            </h4>
            <div className="space-y-2">
              {ai_feedback.weaknesses.map((weakness, i) => (
                <div key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-medium text-amber-900">{weakness.area}</div>
                      <div className="text-sm text-amber-700">{weakness.description}</div>
                    </div>
                  </div>
                  {weakness.improvement_tip && (
                    <div className="mt-2 ml-8 p-2 bg-amber-100/50 rounded text-sm text-amber-800">
                      <Lightbulb className="h-4 w-4 inline mr-1 text-amber-600" />
                      <strong>Tip:</strong> {weakness.improvement_tip}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Concepts to Review */}
        {ai_feedback.key_concepts_to_review?.length > 0 && (
          <div>
            <h4 className="flex items-center gap-2 font-semibold text-violet-700 mb-3">
              <BookOpen className="h-5 w-5" />
              Key Concepts to Review
            </h4>
            <div className="flex flex-wrap gap-2">
              {ai_feedback.key_concepts_to_review.map((concept, i) => (
                <Badge key={i} variant="secondary" className="bg-violet-100 text-violet-800 hover:bg-violet-200">
                  {concept}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Recommended Next Steps */}
        {ai_feedback.recommended_next_steps?.length > 0 && (
          <div>
            <h4 className="flex items-center gap-2 font-semibold text-blue-700 mb-3">
              <ArrowRight className="h-5 w-5" />
              Recommended Next Steps
            </h4>
            <ol className="space-y-2">
              {ai_feedback.recommended_next_steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3 p-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-sm text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Motivational Message */}
        {ai_feedback.motivational_message && (
          <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200 text-center">
            <Sparkles className="h-6 w-6 mx-auto text-violet-500 mb-2" />
            <p className="text-violet-800 font-medium italic">
              &ldquo;{ai_feedback.motivational_message}&rdquo;
            </p>
          </div>
        )}

        {/* Close Button */}
        {onClose && (
          <div className="pt-4">
            <Button onClick={onClose} className="w-full" variant="outline">
              Close Feedback
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssessmentFeedback;
